/**
 * Keeps PostHog in step with the app: who is signed in, which organization
 * is active, and which page is showing. Renders nothing.
 *
 * This bridge is mounted OUTSIDE `RouterProvider` (see AppRoot), so it must
 * never call a router-context hook (`useParams`/`useMatch`/…): doing so reads
 * a null router and throws, which white-screens the whole app. It derives the
 * active organization from the `router` instance it is handed instead.
 */
import { useEffect, useMemo, useSyncExternalStore } from "react";
import { getAnalyticsConsent, subscribeAnalyticsConsent } from "@repo/shared/analyticsConsent";

import { resolveOrgIdFromSegment } from "../orgRouting";
import { readLastPickedOrgId, useOrgs } from "../state/orgs";
import { resolveActiveOrgSegment } from "../threadRoutes";
import {
  analyticsPath,
  capturePageview,
  identifyViewer,
  readPostHogConfig,
  resetViewer,
  startPostHog,
  stopPostHog,
} from "./posthog";
import type { AppRouter } from "../router";

const config = readPostHogConfig(import.meta.env as Record<string, string | undefined>);

/** The current org URL segment, read from the router instance (no hooks). */
function currentOrgSegment(router: AppRouter): string | null {
  const params = (router.state.matches[router.state.matches.length - 1]?.params ??
    {}) as { org?: string };
  return resolveActiveOrgSegment(params);
}

/**
 * `useActiveOrgId` from `../state/orgs`, but reading the router instance the
 * bridge already holds rather than the router *context* it does not have.
 * Reactive: it re-reads on every resolved navigation.
 */
function useActiveOrgIdFromRouter(router: AppRouter): string | null {
  const segment = useSyncExternalStore(
    (onChange) => router.subscribe("onResolved", onChange),
    () => currentOrgSegment(router),
    () => null,
  );
  const { orgs } = useOrgs();
  return useMemo(() => {
    if (segment === null) {
      return readLastPickedOrgId();
    }
    return resolveOrgIdFromSegment(
      segment,
      orgs.map((org) => ({ id: org.id, slug: org.slug })),
    );
  }, [orgs, segment]);
}

export function AnalyticsBridge({ router }: { readonly router: AppRouter }) {
  const consent = useSyncExternalStore(subscribeAnalyticsConsent, getAnalyticsConsent, () => "unknown" as const);
  const { viewer } = useOrgs();
  const activeOrgId = useActiveOrgIdFromRouter(router);

  // Identity first, so the first pageview already carries the person and
  // the organization group. A sign-out clears the identity and cookie so the
  // next person on this browser is not attributed to the last one.
  useEffect(() => {
    if (!config || consent !== "accepted") {
      stopPostHog(config?.key);
      return;
    }
    startPostHog(config);
    if (viewer.userId === null) {
      resetViewer();
      return;
    }
    identifyViewer({
      userId: viewer.userId,
      email: viewer.email,
      organizationId: activeOrgId,
    });
  }, [consent, viewer.userId, viewer.email, activeOrgId]);

  useEffect(() => {
    if (!config || consent !== "accepted") return;
    startPostHog(config);
    const report = () => {
      const pathname = router.state.location.pathname;
      // Mask the org segment only on an actual org route, read from the
      // just-resolved router state — never a value that lags this navigation
      // (a leaving-to-/settings pageview must not be rewritten to /:org).
      const orgSegment =
        currentOrgSegment(router) === null ? null : (pathname.split("/")[1] ?? null);
      capturePageview(analyticsPath(pathname, orgSegment));
    };
    report();
    return router.subscribe("onResolved", report);
  }, [router, consent]);

  return null;
}
